/**
 * Heist API Client
 * Enterprise fetch wrapper with requestId support and standard error handling.
 */

function getCsrfToken() {
    const match = document.cookie.split('; ').find(c => c.startsWith('csrf_token='));
    return match ? match.split('=')[1] : '';
}

// Map technical error codes/messages to friendly, branded language
function friendlyMessage(msg, code, status, url) {
    if (code === 'CSRF_INVALID' || /csrf/i.test(msg)) return 'Your session needs a quick refresh. Please reload the page and try again.';
    if (code === 'RATE_LIMITED' || status === 429) return 'Slow down! Too many attempts. Give it a few minutes and try again.';
    // Auth-route credential errors → login-style message
    if (code === 'INVALID_CREDENTIALS' && url && url.includes('/api/auth/')) return 'That email or password doesn\'t match our records. Double-check and try again.';
    // Non-auth INVALID_CREDENTIALS (integrations, connectors) → pass through server message
    if (code === 'INVALID_CREDENTIALS') return msg || 'The credentials you entered are invalid. Please check and try again.';
    if (code === 'EMAIL_NOT_VERIFIED') return 'Check your inbox — you need to verify your email before logging in.';
    if (code === 'ACCOUNT_LOCKED') return 'This account has been temporarily locked. Contact support if you need help.';
    // For 401 on auth routes (login/register), show the actual server message
    if (status === 401 && url && url.includes('/api/auth/')) return msg || 'That email or password doesn\'t match our records.';
    // Non-auth 401s (e.g. integration test failures) → pass through server message
    if (code === 'AUTH_FAILED' || code === 'TOKEN_EXPIRED') return msg || 'Authentication failed. Please check your credentials.';
    if (code === 'SESSION_EXPIRED' || status === 401) return 'Your session has expired. Please log in again.';
    if (status === 500) return 'Something went wrong on our end. Try again in a moment.';
    // Phase 40 hotfix — BYOK-missing-key responses come back as 503 with a
    // typed code + specific message (e.g. "Connect your ElevenLabs key in
    // Settings → AI Settings"). Pass those through verbatim instead of
    // overriding with the generic outage line. Outage responses (no body
    // or no message) still fall back to the generic copy.
    if (code === 'NO_ELEVENLABS_KEY' || code === 'NO_CREATOMATE_KEY'
        || code === 'NO_RUNWAY_KEY' || code === 'NO_IMAGE_PROVIDER'
        || code === 'NO_VIDEO_PROVIDER') {
        return msg || 'A required AI provider key is missing. Open Settings → AI Settings to connect it.';
    }
    if (status === 503) return msg || 'Heist is briefly unavailable. We\'ll be back in a moment.';
    return msg;
}

async function request(url, options = {}) {
    if (String(url).toLowerCase().includes('undefined')) {
        throw new Error(`UI_CONTRACT_VIOLATION: API route missing (${url})`);
    }
    const forceStub = localStorage.getItem('cp_dev_force_stub') === '1';
    const method = (options.method || 'GET').toUpperCase();
    const needsCsrf = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';

    const activeBrandId = localStorage.getItem('heist_active_brand');
    const defaultOptions = {
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
            ...(forceStub ? { "X-Dev-Force-Stub": "1" } : {}),
            ...(needsCsrf ? { "X-CSRF-Token": getCsrfToken() } : {}),
            ...(activeBrandId ? { "X-Brand-Id": activeBrandId } : {})
        },
    };

    const finalOptions = {
        ...defaultOptions,
        ...options,
        headers: {
            ...defaultOptions.headers,
            ...options.headers,
        },
    };

    if (finalOptions.body && typeof finalOptions.body !== "string" && !(finalOptions.body instanceof FormData)) {
        finalOptions.body = JSON.stringify(finalOptions.body);
    }

    if (finalOptions.body instanceof FormData) {
        delete finalOptions.headers["Content-Type"];
    }

    try {
        const response = await fetch(url, finalOptions);
        const text = await response.text();
        let data;
        try {
            data = text ? JSON.parse(text) : {};
        } catch (e) {
            // Server returned non-JSON (e.g. HTML error page, empty response, CORS block)
            if (response.status >= 500) {
                data = { error: { message: "Something went wrong on our end. Try again in a moment." } };
            } else if (response.status === 0 || !response.ok) {
                data = { error: { message: "Couldn't reach the server. Check your connection and try again." } };
            } else {
                data = { error: { message: "Something unexpected happened. Please try again." } };
            }
        }

        if (!response.ok || data.ok === false) {
            const requestId = response.headers.get("X-Request-Id") || data.requestId || "N/A";

            // Session expired — redirect to login (only if user was previously authenticated)
            // BUT not when the 401 is about THIRD-PARTY credentials (e.g. you entered a bad
            // LinkedIn / WordPress / Meta token and the server reports back 401 from that
            // service). Logging the user out in that case loses their session for no reason.
            const respCode = data.error?.code || data.code;
            const thirdPartyCredFailure = [
                'AUTH_FAILED',           // generic provider auth failure
                'INVALID_CREDENTIALS',   // creds malformed / wrong shape
                'TOKEN_EXPIRED',         // provider token expired
                'PERMISSION_DENIED',     // app lacks required scope on the provider
                'VALIDATION_FAILED'      // generic validate-then-save 401
            ].includes(respCode);

            if (response.status === 401 && !url.includes('/api/auth/') && window.__auth__ && !thirdPartyCredFailure) {
                console.warn('[AUTH] Session expired — redirecting to login');
                window.__auth__ = null;
                window.location.hash = '#login';
                const authError = new Error('Session expired. Please log in again.');
                authError.status = 401;
                authError.requestId = requestId;
                authError.code = 'SESSION_EXPIRED';
                throw authError;
            }

            // Tier limit reached — show upgrade modal (Phase 35D)
            if (response.status === 403) {
                const code = data.error?.code || data.code;
                if (['POST_LIMIT_REACHED', 'CAMPAIGN_LIMIT_REACHED', 'BRAND_LIMIT_REACHED', 'TRIAL_EXPIRED'].includes(code)) {
                    window.dispatchEvent(new CustomEvent('heist:upgrade-needed', { detail: { error: data.error?.message || data.error, code } }));
                }
            }

            if (response.status === 404) {
                const msg = data.error?.message || data.error || `Route not found (${url})`;
                throw new Error(`API_ERROR: ${msg} [RID: ${requestId}]`);
            }

            const rawMsg = data.error?.message || data.error || response.statusText;
            const errorCode = data.error?.code || data.code;
            const error = new Error(friendlyMessage(rawMsg, errorCode, response.status, url));
            error.status = response.status;
            error.requestId = requestId;
            error.code = errorCode;
            throw error;
        }

        return data;
    } catch (err) {
        if (!err.requestId) {
            console.error("[CLIENT_ERROR]", err.message);
            // Network/CORS errors produce TypeError with no status — make user-friendly
            if (err instanceof TypeError && err.message === 'Failed to fetch') {
                const friendly = new Error("Couldn't connect to the server. Check your internet connection and try again.");
                friendly.status = 0;
                friendly.code = 'NETWORK_ERROR';
                throw friendly;
            }
        }
        throw err;
    }
}

async function requestRaw(url, options = {}) {
    const defaultOptions = { credentials: "include" };
    const finalOptions = { ...defaultOptions, ...options };
    const response = await fetch(url, finalOptions);
    if (!response.ok) {
        const requestId = response.headers.get("X-Request-Id") || "N/A";
        throw new Error(`API_ERROR: ${response.statusText} [RID: ${requestId}]`);
    }
    return response;
}

/**
 * Serialize params object to query string, handling arrays as repeated keys.
 * e.g. { status: ['draft','scheduled'] } => "?status=draft&status=scheduled"
 */
function buildQuery(params) {
    const sp = new URLSearchParams();
    if (!params) return "";
    for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null || v === "") continue;
        if (Array.isArray(v)) {
            for (const item of v) {
                if (item === undefined || item === null || item === "") continue;
                sp.append(k, String(item));
            }
        } else {
            sp.set(k, String(v));
        }
    }
    const qs = sp.toString();
    return qs ? `?${qs}` : "";
}

/**
 * Emit a content-mutation event so pages (Analytics, Library, Calendar,
 * Command Center) can refetch derived data after the user changes content.
 * Uses CustomEvent on window so any mounted page can subscribe/unsubscribe.
 */
function emitContentChanged(detail = {}) {
    try {
        window.dispatchEvent(new CustomEvent('heist:content-changed', { detail }));
    } catch (_) {}
}

/**
 * Wrap a mutation promise so it emits 'heist:content-changed' on success.
 */
function withContentMutation(promise, detail = {}) {
    return promise.then((res) => { emitContentChanged(detail); return res; });
}

export const client = {
    authMe: () => request("/api/auth/me"),
    authLogin: (payload) => request("/api/auth/login", { method: "POST", body: payload }),
    authRegister: (payload) => request("/api/auth/signup", { method: "POST", body: payload }),
    authLogout: () => request("/api/auth/logout", { method: "POST" }),
    authGoogleCallback: (credential, extra = {}) => request("/api/auth/google/callback", { method: "POST", body: { credential, ...extra } }),
    funnelEvent: (event) => request("/api/funnel/event", { method: "POST", body: { event } }),
    authGoogleConfig: () => request("/api/auth/google/config"),
    deleteAccount: (password) => request("/api/auth/account", { method: "DELETE", body: { password } }),
    exportMyData: () => requestRaw("/api/auth/export"),

    // Memory & The Brain
    getMemory: () => request("/api/memory"),
    updateProfile: (payload) => request("/api/memory", { method: "POST", body: payload }),
    addFact: (payload) => request("/api/memory/facts", { method: "POST", body: payload }),
    deleteFact: (id) => request(`/api/memory/facts/${id}`, { method: "DELETE" }),
    uploadDocument: (formData) => request("/api/memory/documents", { method: "POST", body: formData }),
    deleteDocument: (id) => request(`/api/memory/documents/${id}`, { method: "DELETE" }),
    getDocumentTrash: () => request("/api/memory/documents/trash"),
    restoreDocument: (id) => request(`/api/memory/documents/${id}/restore`, { method: "POST" }),
    permanentDeleteDocument: (id) => request(`/api/memory/documents/${id}/permanent`, { method: "DELETE" }),
    emptyDocumentTrash: () => request("/api/memory/documents/trash/empty", { method: "DELETE" }),
    scanUrl: (url) => request("/api/memory/scan-url", { method: "POST", body: { url } }),
    suggestProfile: (url) => request("/api/memory/suggest-profile", { method: "POST", body: { url } }),
    connectUrl: (url) => request("/api/memory/connect-url", { method: "POST", body: { url } }),
    getConnectedSources: () => request("/api/memory/connected-sources"),
    disconnectSource: (id) => request(`/api/memory/connected-sources/${id}`, { method: "DELETE" }),
    rescanSource: (id) => request(`/api/memory/connected-sources/${id}/rescan`, { method: "POST" }),
    getPersonas: () => request("/api/memory/personas"),
    addPersona: (data) => request("/api/memory/personas", { method: "POST", body: data }),
    updatePersona: (id, data) => request(`/api/memory/personas/${id}`, { method: "PUT", body: data }),
    deletePersona: (id) => request(`/api/memory/personas/${id}`, { method: "DELETE" }),
    // Connected Sources
    googleDocsAuth: () => request("/api/integrations/google/auth"),
    googleDocsStatus: () => request("/api/integrations/google/status"),
    googleDocsList: () => request("/api/integrations/google/docs"),
    googleDocsFolderContents: (folderId) => request(`/api/integrations/google/folder/${folderId}`),
    googleDocsSync: (docIds, folderIds) => request("/api/integrations/google/sync", { method: "POST", body: { docIds, folderIds } }),
    googleDocsDisconnect: () => request("/api/integrations/google/disconnect", { method: "POST" }),
    notionAuth: () => request("/api/integrations/notion/auth"),
    notionStatus: () => request("/api/integrations/notion/status"),
    notionPages: () => request("/api/integrations/notion/pages"),
    notionSync: (pageIds) => request("/api/integrations/notion/sync", { method: "POST", body: { pageIds } }),
    notionDisconnect: () => request("/api/integrations/notion/disconnect", { method: "POST" }),
    getBrainSummary: () => request("/api/memory/brain-summary"),
    brainChat: (payload) => request("/api/memory/chat", { method: "POST", body: payload }),
    getFactTargets: (factId) => request(`/api/memory/facts/${factId}/targets`),
    setFactTargets: (factId, payload) => request(`/api/memory/facts/${factId}/targets`, { method: "POST", body: payload }),
    deleteFactTarget: (factId, targetRowId) => request(`/api/memory/facts/${factId}/targets/${targetRowId}`, { method: "DELETE" }),
    generateBatch: (payload) => request("/api/generate/batch", { method: "POST", body: payload }),
    enhanceContent: (payload) => request("/api/generate/batch", { method: "POST", body: { ...payload, mode: "assist" } }),
    rewriteContentItem: (contentItemId) => request("/api/generate/rewrite", { method: "POST", body: { contentItemId } }),
    rewriteCampaign: (campaignId) => request("/api/generate/rewrite-campaign", { method: "POST", body: { campaignId } }),

    // AI Media Generation
    generateAiImage: (payload) => request("/api/generate/image", { method: "POST", body: payload }),
    generateAiVideo: (payload) => request("/api/generate/video", { method: "POST", body: payload }),
    getVideoGenStatus: (id) => request(`/api/generate/video/${id}/status`),
    getMediaAvailability: () => request("/api/generate/media-availability"),

    // Phase 37 — faceless video pipeline (script → voiceover → slideshow).
    // generateFacelessVideo is synchronous on the HTTP level and can take
    // 30–90s; the request uses an extended timeout so the response arrives
    // before the browser bails. A polling variant lands in a later commit.
    // Phase 37 Commit 7 — blog video derivative listing
    getBlogVideoDerivatives: (blogId) => request(`/api/blog/${encodeURIComponent(blogId)}/video-derivatives`),

    listFacelessVideoVoices: () => request("/api/faceless-video/voices"),
    generateFacelessVideo: (payload) => request("/api/faceless-video/generate", { method: "POST", body: payload, timeoutMs: 180000 }),
    // Async variant — returns { job_id } immediately; poll getFacelessVideoJob
    // for real stage/percent progress until status is succeeded/failed.
    generateFacelessVideoAsync: (payload) => request("/api/faceless-video/generate-async", { method: "POST", body: payload }),
    getFacelessVideoJob: (jobId) => request(`/api/faceless-video/job/${encodeURIComponent(jobId)}`),
    generateVoiceover: (payload) => request("/api/faceless-video/voiceover", { method: "POST", body: payload, timeoutMs: 90000 }),
    // Phase 37 Commit 5 — polish helpers
    generateVideoScript: (payload) => request("/api/faceless-video/script", { method: "POST", body: payload }),
    getDefaultVideoVoice: (brandId) => request(`/api/faceless-video/voice-default${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''}`),
    setDefaultVideoVoice: (payload) => request("/api/faceless-video/voice-default", { method: "PUT", body: payload }),
    estimateFacelessVideoCost: (chars, duration, opts = {}) => {
        const params = new URLSearchParams({ chars: String(chars), duration: String(duration || 60) });
        if (opts.style) params.set('style', opts.style);
        if (opts.clipCount) params.set('clip_count', String(opts.clipCount));
        if (opts.clipDuration) params.set('clip_duration', String(opts.clipDuration));
        if (opts.aiVideoModel) params.set('ai_video_model', opts.aiVideoModel);
        return request(`/api/faceless-video/cost-estimate?${params.toString()}`);
    },

    healthAi: () => request("/api/health/ai"),
    getDashboardSummary: () => request("/api/dashboard/summary"),
    getDashboardTrends: () => request("/api/dashboard/trends"),

    // Content Library
    getContentLibrary: (params = {}) => {
        return request(`/api/content/library${buildQuery(params)}`);
    },
    saveToLibrary: (payload) => withContentMutation(request("/api/content/save", { method: "POST", body: payload }), { action: 'save' }),
    deleteFromLibrary: (id) => withContentMutation(request(`/api/content/items/${id}`, { method: "DELETE" }), { action: 'delete', id }),
    deleteContentItem: (id) => withContentMutation(request(`/api/content/items/${id}`, { method: "DELETE" }), { action: 'delete', id }),
    updateContentItem: (id, payload) => withContentMutation(request(`/api/content/items/${id}`, { method: "PUT", body: payload }), { action: 'update', id, payload }),
    updateLibraryItem: (id, payload) => withContentMutation(request(`/api/content/items/${id}`, { method: "PUT", body: payload }), { action: 'update', id, payload }),
    repurposeToBlog: (sourceIds) => withContentMutation(request("/api/content/repurpose-to-blog", { method: "POST", body: { sourceIds } }), { action: 'repurpose' }),

    // Blog snippets — reusable boilerplate HTML blocks injected into every
    // generated blog post at a chosen position (FAQ, disclaimer, footer, etc.)
    listBlogSnippets: () => request("/api/blog-snippets"),
    createBlogSnippet: (body) => withContentMutation(request("/api/blog-snippets", { method: "POST", body }), { action: 'blog-snippet-create' }),
    updateBlogSnippet: (id, body) => withContentMutation(request(`/api/blog-snippets/${id}`, { method: "PUT", body }), { action: 'blog-snippet-update', id }),
    deleteBlogSnippet: (id) => withContentMutation(request(`/api/blog-snippets/${id}`, { method: "DELETE" }), { action: 'blog-snippet-delete', id }),

    // Stock media (Pexels videos, etc.) — proxied via server so keys stay private
    searchStockVideos: (params = {}) => request(`/api/media/stock/videos${buildQuery(params)}`),

    // Blog Editor
    listBlogPosts: (params = {}) => {
        return request(`/api/blog/posts${buildQuery(params)}`);
    },
    getBlogPost: (id) => request(`/api/blog/posts/${id}`),
    migrateLegacyBlog: (id) => request(`/api/blog/posts/migrate-from-content-item/${id}`, { method: "POST" }),
    // Blog tag registry (per-brand category pills)
    listBlogTags: () => request('/api/blog/tags'),
    createBlogTag: (tag) => request('/api/blog/tags', { method: 'POST', body: tag }),
    updateBlogTag: (id, patch) => request(`/api/blog/tags/${id}`, { method: 'PUT', body: patch }),
    deleteBlogTag: (id) => request(`/api/blog/tags/${id}`, { method: 'DELETE' }),
    saveBlogPost: (post) => {
        const method = post.id ? "PUT" : "POST";
        const url = post.id ? `/api/blog/posts/${post.id}` : "/api/blog/posts";
        return withContentMutation(request(url, { method, body: post }), { action: 'blog-save', id: post.id });
    },
    generateBlogDraft: (payload) => request("/api/blog/generate", { method: "POST", body: payload }),
    scoreBlogPost: (payload) => request("/api/blog/score", { method: "POST", body: payload }),
    alignBlogPost: (payload) => request("/api/blog/align", { method: "POST", body: payload }),
    generateSocialFromBlog: (payload) => request("/api/blog/generate-social", { method: "POST", body: payload }),
    uploadBlogImage: (file) => {
        var formData = new FormData();
        formData.append('image', file);
        return request('/api/blog/upload-image', { method: 'POST', body: formData });
    },
    getBlogImages: () => request('/api/blog/images'),

    // WordPress integration
    getWpCategories: () => request('/api/blog/wp-categories'),
    getWpTags: () => request('/api/blog/wp-tags'),
    createWpTag: (name) => request('/api/blog/wp-tags', { method: 'POST', body: { name } }),
    syncBlogToWp: (postId) => request(`/api/blog/posts/${postId}/sync-to-wp`, { method: 'POST' }),

    // Campaigns
    getCampaigns: () => request("/api/campaigns"),
    getCampaignDetail: (id) => request(`/api/campaigns/${id}`),
    createCampaign: (payload) => withContentMutation(request("/api/campaigns", { method: "POST", body: payload }), { action: 'campaign-create' }),
    saveToCampaign: (campaignId, payload) => withContentMutation(request(`/api/campaigns/${campaignId}/items`, { method: "POST", body: payload }), { action: 'campaign-add-item', campaignId }),
    deleteCampaign: (id) => withContentMutation(request(`/api/campaigns/${id}`, { method: "DELETE" }), { action: 'campaign-delete', id }),
    updateCampaignItem: (campaignId, itemId, payload) => withContentMutation(request(`/api/campaigns/${campaignId}/items/${itemId}`, { method: "PUT", body: payload }), { action: 'campaign-item-update', campaignId, itemId }),
    // Phase 31F: Campaign-level engagement rollup
    getCampaignsAnalyticsBulk: () => request("/api/campaigns/analytics"),
    getCampaignAnalyticsDetail: (id) => request(`/api/campaigns/${id}/analytics`),
    // Phase 31G: Client PDF report export. Returns the URL so callers can window.open().
    engagementReportPdfUrl: ({ days = 30, campaignId } = {}) => {
        const q = new URLSearchParams();
        q.set('days', String(days));
        if (campaignId) q.set('campaignId', campaignId);
        return `/api/reports/engagement.pdf?${q.toString()}`;
    },
    // Phase 36B: edit hints — non-secret values + secret-set flags for pre-fill.
    getIntegrationEditHints: (provider) => request(`/api/integrations/${provider}/edit`),
    // Phase 31.5B: Website/Blog tracked-site integrations
    integrationsSiteDetect: (url) => request('/api/integrations/site/detect', { method: 'POST', body: { url } }),
    integrationsSiteAdd: (payload) => request('/api/integrations/site', { method: 'POST', body: payload }),
    integrationsSitesList: () => request('/api/integrations/sites'),
    integrationsSiteDelete: (id) => request(`/api/integrations/site/${id}`, { method: 'DELETE' }),

    // Schedule & Calendar
    getScheduleCalendar: (params = {}) => {
        return request(`/api/schedule/calendar${buildQuery(params)}`);
    },
    updateScheduledPost: (id, payload) => withContentMutation(request(`/api/schedule/items/${id}`, { method: "PUT", body: payload }), { action: 'schedule-update', id }),
    deleteScheduledPost: (id) => withContentMutation(request(`/api/schedule/items/${id}`, { method: "DELETE" }), { action: 'schedule-delete', id }),

    // Personal calendar items (reminders) — private to creator
    getPersonalEvents: (params = {}) => request(`/api/personal-events${buildQuery(params)}`),
    createPersonalEvent: (payload) => request('/api/personal-events', { method: 'POST', body: payload }),
    updatePersonalEvent: (id, payload) => request(`/api/personal-events/${id}`, { method: 'PUT', body: payload }),
    deletePersonalEvent: (id) => request(`/api/personal-events/${id}`, { method: 'DELETE' }),

    // Analytics
    getAnalyticsOverview: (params = {}) => {
        return request(`/api/analytics/overview${buildQuery(params)}`);
    },
    getAnalyticsFollowersSeries: (params = {}) => {
        return request(`/api/analytics/followers/series${buildQuery(params)}`);
    },
    runAnalyticsFollowersSnapshot: (payload) => request("/api/analytics/followers/run", { method: "POST", body: payload }),
    getAnalyticsContentStats: () => request("/api/analytics/content-stats"),
    getAnalyticsPerformance: (params) => request(`/api/analytics/performance${buildQuery(params)}`),
    getAnalyticsPerformancePosts: (params) => request(`/api/analytics/performance/posts${buildQuery(params)}`),
    getAnalyticsPerformanceTrends: (params) => request(`/api/analytics/performance/trends${buildQuery(params)}`),
    syncAnalyticsPerformance: () => request("/api/analytics/performance/sync", { method: "POST" }),
    getAnalyticsLastSynced: () => request("/api/analytics/performance/last-synced"),
    getAnalyticsSyncStatus: () => request("/api/analytics/performance/sync-status"),

    // Integrations
    getIntegrations: () => request("/api/integrations"),
    connectIntegration: (provider, payload) => request(`/api/integrations/${provider}`, { method: "POST", body: payload }),
    disconnectIntegration: (provider) => request(`/api/integrations/${provider}`, { method: "DELETE" }),
    updateLinkedInTarget: (payload) => request(`/api/integrations/linkedin/target`, { method: "PUT", body: payload }),
    testIntegration: (provider) => request(`/api/integrations/${provider}/test`, { method: "POST" }),

    // OAuth flows (Meta & LinkedIn)
    getMetaOAuthUrl: () => request("/api/auth/meta"),
    getMetaOAuthStatus: () => request("/api/auth/meta/status"),
    getLinkedInOAuthUrl: () => request("/api/auth/linkedin"),
    getLinkedInOAuthStatus: () => request("/api/auth/linkedin/status"),

    // AI Settings
    getAiSettings: () => request("/api/ai-settings"),
    updateAiSettings: (payload) => request("/api/ai-settings", { method: "PUT", body: payload }),
    clearAiSettingsKey: () => request("/api/ai-settings/key", { method: "DELETE" }),
    getServerKeyPolicy: () => request("/api/ai-settings/server-policy"),
    updateServerKeyPolicy: (policy) => request("/api/ai-settings/server-policy", { method: "PUT", body: { policy } }),
    updateAiModel: (model) => request("/api/ai-settings/model", { method: "PUT", body: { model } }),

    // Chat (Q4)
    getChatProjects: () => request("/api/chat/projects"),
    createChatProject: (payload) => request("/api/chat/projects", { method: "POST", body: payload }),
    updateChatProject: (id, payload) => request(`/api/chat/projects/${id}`, { method: "PUT", body: payload }),
    deleteChatProject: (id) => request(`/api/chat/projects/${id}`, { method: "DELETE" }),

    getChatThreads: (params = {}) => request(`/api/chat/threads${buildQuery(params)}`),
    createChatThread: (payload) => request("/api/chat/threads", { method: "POST", body: payload }),
    updateChatThread: (id, payload) => request(`/api/chat/threads/${id}`, { method: "PUT", body: payload }),
    deleteChatThread: (id) => request(`/api/chat/threads/${id}`, { method: "DELETE" }),

    getChatMessages: (threadId, params = {}) => request(`/api/chat/threads/${threadId}/messages${buildQuery(params)}`),
    chatSend: (threadId, payload) => request(`/api/chat/threads/${threadId}/send`, { method: "POST", body: payload }),
    chatSearch: (params = {}) => request(`/api/chat/search${buildQuery(params)}`),

    shareChatThread: (id) => request(`/api/chat/threads/${id}/share`, { method: "POST" }),
    revokeChatShare: (shareId) => request(`/api/chat/shares/${shareId}`, { method: "DELETE" }),

    exportChatThread: (id, format = 'json') => requestRaw(`/api/chat/threads/${id}/export?format=${format}`),
    exportChatProject: (id, format = 'json') => requestRaw(`/api/chat/projects/${id}/export?format=${format}`),

    // Publish actions
    publishNow: (postId) => request(`/api/publish/${postId}`, { method: 'POST' }),
    retryPublish: (postId) => request(`/api/publish/${postId}/retry`, { method: 'POST' }),
    cancelPublish: (postId) => request(`/api/publish/${postId}/cancel`, { method: 'POST' }),
    getPublishQueue: (params) => request(`/api/publish/queue${buildQuery(params)}`),

    // API keys (for MCP / external-agent access)
    listApiKeys: () => request('/api/api-keys'),
    createApiKey: (payload) => request('/api/api-keys', { method: 'POST', body: payload }),
    revokeApiKey: (id) => request(`/api/api-keys/${id}`, { method: 'DELETE' }),

    // Schedule Rules
    getScheduleRules: () => request('/api/schedule/rules'),
    createScheduleRule: (payload) => request('/api/schedule/rules', { method: 'POST', body: payload }),
    updateScheduleRule: (id, payload) => request(`/api/schedule/rules/${id}`, { method: 'PUT', body: payload }),
    deleteScheduleRule: (id) => request(`/api/schedule/rules/${id}`, { method: 'DELETE' }),

    // Debug Tools (Phase 5)
    getAiDebugContext: () => request("/api/ai/debug/ai-context"),

    // Admin
    adminGetUsers: (params) => request(`/api/admin/users${buildQuery(params)}`),
    adminGetUser: (id) => request(`/api/admin/users/${id}`),
    adminUpdateTier: (id, data) => request(`/api/admin/users/${id}/tier`, { method: "PUT", body: data }),
    adminResetPassword: (id, data) => request(`/api/admin/users/${id}/reset-password`, { method: "PUT", body: data }),
    adminGetStats: () => request("/api/admin/stats"),
    adminGetDashboardSummary: () => request("/api/admin/dashboard/summary"),
    adminGetFunnel: (weeks = 12) => request(`/api/admin/funnel?weeks=${weeks}`),
    adminGetDashboardEvents: (limit = 50) => request(`/api/admin/dashboard/recent-events?limit=${limit}`),
    adminGetStorage: () => request("/api/admin/storage"),
    adminGetDeadLetter: () => request("/api/admin/dead-letter"),
    adminRetryDeadLetter: (postId) => request(`/api/admin/dead-letter/${postId}/retry`, { method: "POST" }),
    adminRetryAllDeadLetter: () => request("/api/admin/dead-letter/retry-all", { method: "POST" }),
    adminGetIntegrationsHealth: () => request("/api/admin/integrations-health"),
    adminGetOpsJobs: () => request("/api/admin/ops/jobs"),
    adminRunOpsJob: (name) => request(`/api/admin/ops/jobs/${name}/run`, { method: "POST" }),
    adminOpsTestAlert: () => request("/api/admin/ops/test-alert", { method: "POST" }),
    adminImpersonate: (id) => request(`/api/admin/users/${id}/impersonate`, { method: "POST" }),
    adminEndImpersonation: () => request('/api/admin/end-impersonation', { method: "POST" }),
    adminSetStatus: (id, data) => request(`/api/admin/users/${id}/status`, { method: "PUT", body: data }),
    adminRestoreUser: (id) => request(`/api/admin/users/${id}/restore`, { method: "POST" }),
    adminGrantTrial: (id) => request(`/api/admin/users/${id}/grant-trial`, { method: "POST" }),
    adminGrantFree: (id, data) => request(`/api/admin/users/${id}/grant-free`, { method: "POST", body: data }),
    adminBulkStatus: (data) => request("/api/admin/users/bulk-status", { method: "POST", body: data }),
    adminBulkPermanentDelete: (data) => request("/api/admin/users/bulk-permanent-delete", { method: "POST", body: data }),
    adminPermanentDelete: (id, data) => request(`/api/admin/users/${id}/permanent`, { method: "DELETE", body: data }),
    adminCleanupEmpty: () => request("/api/admin/users/cleanup-empty", { method: "POST" }),
    adminReExtractVault: () => request("/api/admin/re-extract-vault", { method: "POST" }),

    // Admin — Workspace management
    adminGetWorkspaces: (params) => request(`/api/admin/workspaces${buildQuery(params)}`),
    adminGetWorkspace: (id) => request(`/api/admin/workspaces/${id}`),
    adminUpdateWorkspace: (id, data) => request(`/api/admin/workspaces/${id}`, { method: "PUT", body: data }),
    adminCreateWorkspace: (data) => request("/api/admin/workspaces/create", { method: "POST", body: data }),
    adminAddWorkspaceMember: (wsId, data) => request(`/api/admin/workspaces/${wsId}/add-member`, { method: "POST", body: data }),
    adminRemoveWorkspaceMember: (wsId, userId) => request(`/api/admin/workspaces/${wsId}/members/${userId}`, { method: "DELETE" }),
    adminUpdateWorkspaceMemberRole: (wsId, userId, data) => request(`/api/admin/workspaces/${wsId}/members/${userId}`, { method: "PUT", body: data }),
    adminUpdateAgencyBrain: (wsId, data) => request(`/api/admin/workspaces/${wsId}/agency-brain`, { method: "PUT", body: data }),

    // Avatar
    uploadAvatar: (formData) => request("/api/auth/avatar", { method: "POST", body: formData }),

    // Notifications (Phase 27B)
    getNotifications: () => request("/api/notifications"),
    getUnreadCount: () => request("/api/notifications/unread-count"),
    markNotificationRead: (id) => request(`/api/notifications/${id}/read`, { method: "PUT" }),
    markAllNotificationsRead: () => request("/api/notifications/read-all", { method: "PUT" }),
    deleteNotification: (id) => request(`/api/notifications/${id}`, { method: "DELETE" }),

    // Search (Phase 28A)
    search: (q) => request(`/api/search?q=${encodeURIComponent(q)}`),

    // User feedback / support tickets
    submitFeedback: (payload) => request('/api/feedback', { method: 'POST', body: payload }),
    adminListFeedback: (params) => request(`/api/feedback${buildQuery(params)}`),
    adminUpdateFeedback: (id, data) => request(`/api/feedback/${id}`, { method: 'PATCH', body: data }),

    // Discount codes
    discountList: () => request("/api/discount"),
    discountCreate: (data) => request("/api/discount", { method: "POST", body: data }),
    discountUpdate: (id, data) => request(`/api/discount/${id}`, { method: "PUT", body: data }),
    discountDelete: (id) => request(`/api/discount/${id}`, { method: "DELETE" }),
    discountRedeem: (code) => request("/api/discount/redeem", { method: "POST", body: { code } }),

    // Email verification + password reset
    forgotPassword: (email) => request("/api/auth/forgot-password", { method: "POST", body: { email } }),
    resetPassword: (token, newPassword) => request("/api/auth/reset-password", { method: "POST", body: { token, newPassword } }),
    sendVerification: () => request("/api/auth/send-verification", { method: "POST", body: {} }),
    verifyEmail: (code) => request("/api/auth/verify-email", { method: "POST", body: { code } }),

    // Billing
    getBillingStatus: () => request("/api/billing/status"),
    getBillingUsageRecap: () => request("/api/billing/usage-recap"),
    createCheckout: (tier, cycle, discountCode) => request("/api/billing/create-checkout", { method: "POST", body: { tier, cycle, discount_code: discountCode } }),
    createPortalSession: () => request("/api/billing/create-portal", { method: "POST", body: {} }),

    // Onboarding
    completeOnboarding() { return request('/api/auth/complete-onboarding', { method: 'POST', body: {} }); },
    completeTour(opts) { return request('/api/auth/complete-tour', { method: 'POST', body: opts || {} }); },
    completeSetup() { return request('/api/auth/complete-setup', { method: 'POST', body: {} }); },
    setupSkipped() { return request('/api/auth/setup-skipped', { method: 'POST', body: {} }); },

    // Multi-Brand (Phase 24)
    getBrands() { return request('/api/brands'); },
    createBrand(name) { return request('/api/brands', { method: 'POST', body: { name } }); },
    renameBrand(id, name) { return request(`/api/brands/${id}`, { method: 'PUT', body: { name } }); },
    deleteBrand(id) { return request(`/api/brands/${id}`, { method: 'DELETE' }); },
    switchBrand(id) { return request(`/api/brands/${id}/switch`, { method: 'POST', body: {} }); },
    togglePauseBrand(id) { return request(`/api/brands/${id}/pause`, { method: 'PUT', body: {} }); },
    // Stock media curation policy
    getStockPolicy(brandId) { return request(`/api/brands/${brandId}/stock-policy`); },
    updateStockPolicy(brandId, patch) { return request(`/api/brands/${brandId}/stock-policy`, { method: 'PUT', body: patch }); },
    blockStockMedia(brandId, url, reason) { return request(`/api/brands/${brandId}/stock-blocklist`, { method: 'POST', body: { url, reason } }); },
    unblockStockMedia(brandId, target) { return request(`/api/brands/${brandId}/stock-blocklist`, { method: 'DELETE', body: typeof target === 'string' && /^blk_/.test(target) ? { id: target } : { url: target } }); },

    // Workspace / Team (Agency collaboration)
    getWorkspace() { return request('/api/workspace'); },
    createWorkspace(name) { return request('/api/workspace/create', { method: 'POST', body: { name } }); },
    inviteMember({ email, role, brandIds }) { return request('/api/workspace/invite', { method: 'POST', body: { email, role, brandIds } }); },
    acceptInvite(token) { return request('/api/workspace/accept-invite', { method: 'POST', body: { token } }); },
    updateMemberRole(userId, role) { return request(`/api/workspace/members/${userId}`, { method: 'PUT', body: { role } }); },
    removeMember(userId) { return request(`/api/workspace/members/${userId}`, { method: 'DELETE' }); },
    updateMemberBrands(userId, brands) { return request(`/api/workspace/members/${userId}/brands`, { method: 'PUT', body: { brands } }); },
    resetMemberPassword(userId, password) { return request(`/api/workspace/members/${userId}/reset-password`, { method: 'PUT', body: { password } }); },
    getAgencyBrain() { return request('/api/workspace/agency-brain'); },
    updateAgencyBrain(profile) { return request('/api/workspace/agency-brain', { method: 'PUT', body: profile }); },
    revokeInvite(id) { return request(`/api/workspace/revoke-invite/${id}`, { method: 'POST', body: {} }); },

    // Content Approvals
    getApprovals(params) { return request(`/api/approvals${buildQuery(params)}`); },
    submitForApproval(contentItemId) { return request('/api/approvals/submit', { method: 'POST', body: { contentItemId } }); },
    approveContent(id, note) { return request(`/api/approvals/${id}/approve`, { method: 'POST', body: { note } }); },
    rejectContent(id, note) { return request(`/api/approvals/${id}/reject`, { method: 'POST', body: { note } }); },
    getApprovalStats() { return request('/api/approvals/stats'); },

    // Activity Log
    getActivityLog(params) { return request('/api/workspace/activity?' + buildQuery(params)); },

    // Seat purchases
    addSeats(seats) { return request('/api/billing/add-seats', { method: 'POST', body: { seats } }); },

    // Team stats
    // buildQuery already prepends "?" — appending it after a literal "?" produced
    // "team-stats??range=30d", which Express parsed as the key "?range", so the
    // range filter was silently ignored server-side.
    getTeamStats(params) { return request('/api/workspace/team-stats' + buildQuery(params)); },

    // Client View / Brand Sharing
    createBrandShare(data) { return request('/api/client-view/shares', { method: 'POST', body: data }); },
    getBrandShares() { return request('/api/client-view/shares'); },
    revokeBrandShare(id) { return request(`/api/client-view/shares/${id}/revoke`, { method: 'POST', body: {} }); },
    getShareComments(shareId) { return request(`/api/client-view/shares/${shareId}/comments`); },
    getClientView(token) { return request(`/api/client-view/public/${token}`); },
    submitClientFeedback(token, data) { return request(`/api/client-view/public/${token}/comment`, { method: 'POST', body: data }); },

    // Workspace Media
    getWorkspaceMedia(params) { return request('/api/workspace/media?' + buildQuery(params)); },
    uploadWorkspaceMedia(formData) {
        const activeBrandId = localStorage.getItem('heist_active_brand');
        const csrfToken = getCsrfToken();
        return fetch('/api/workspace/media', {
            method: 'POST',
            headers: {
                ...(activeBrandId ? { 'X-Brand-Id': activeBrandId } : {}),
                ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
            },
            credentials: 'include',
            body: formData
        }).then(r => r.json());
    },
    updateWorkspaceMedia(id, updates) { return request(`/api/workspace/media/${id}`, { method: 'PUT', body: updates }); },
    deleteWorkspaceMedia(id) { return request(`/api/workspace/media/${id}`, { method: 'DELETE' }); },
    registerStockMedia(body) { return request('/api/workspace/media/register-stock', { method: 'POST', body }); },
    touchWorkspaceMedia(id) { return request(`/api/workspace/media/${id}/touch`, { method: 'POST' }); },

    // Performance signals
    recordPerformance(itemId, score, note) { return request(`/api/content/items/${itemId}/performance`, { method: 'POST', body: { score, note } }); },

    // Data integrity
    getTrash: () => request('/api/content/trash'),
    restoreItem: (id) => request(`/api/content/items/${id}/restore`, { method: 'POST' }),
    permanentDeleteItem: (id) => request(`/api/content/items/${id}/permanent`, { method: 'DELETE' }),
    purgeTrash: (ids) => request('/api/content/trash/purge', { method: 'POST', body: { ids } }),
    emptyTrash: () => request('/api/content/trash/purge', { method: 'POST', body: { all: true } }),
    getVersions: (id) => request(`/api/content/items/${id}/versions`),
    revertVersion: (id, versionId) => request(`/api/content/items/${id}/revert`, { method: 'POST', body: { versionId } }),

    // Generic request helper
    request,

    // Video Upload
    initVideoUpload(data) { return request('/api/video/init', { method: 'POST', body: data }); },
    finalizeVideoUpload(uploadId) { return request(`/api/video/${uploadId}/finalize`, { method: 'POST', body: {} }); },
    getVideos(params) { return request('/api/video?' + new URLSearchParams(params || {}).toString()); },
    getVideo(id) { return request(`/api/video/${id}`); },
    deleteVideo(id) { return request(`/api/video/${id}`, { method: 'DELETE' }); },
    getVideoStorageUsage() { return request('/api/video/storage/usage'); },

    // TikTok
    getTikTokStatus() { return request('/api/auth/tiktok/status'); },
    connectTikTok() { return request('/api/auth/tiktok'); },

    // YouTube
    getYouTubeStatus() { return request('/api/auth/youtube/status'); },
    connectYouTube() { return request('/api/auth/youtube'); },
    connectWordPressCom(blog) { return request('/api/auth/wordpress' + (blog ? '?blog=' + encodeURIComponent(blog) : '')); },

    // Pinterest
    getPinterestStatus() { return request('/api/auth/pinterest/status'); },
    connectPinterest() { return request('/api/auth/pinterest'); },
    listPinterestBoards() { return request('/api/auth/pinterest/boards'); },

    // Reddit
    getRedditStatus() { return request('/api/auth/reddit/status'); },
    connectReddit() { return request('/api/auth/reddit'); },

    // Aliases for backward compatibility
    deleteThread: (id) => request(`/api/chat/threads/${id}`, { method: "DELETE" }),
};
